/**
 * Returns the current user's task scope in the active region: tasks assigned
 * to them personally, unioned with their team's tasks. Backed by `GET /tasks/mine`, which resolves
 * the caller from auth and runs a single OR query server-side — so dedup,
 * pagination and counts are all handled by the backend.
 *
 * Clinic scoping for the team side:
 * - `clinicId` provided → narrow to that clinic.
 * - `clinicId` omitted (`undefined`) → fall back to the user's default clinic
 *   preference. Used by the sidebar badge where there's no picker.
 * - `clinicId === null` → no clinic narrowing (all clinics).
 *
 * The backend ignores clinic scoping entirely for non-clinic-bound roles, so
 * we don't need to special-case the role here.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import type { ClinicId, TaskListStatus, TaskType } from '@snootsvet/shared'

import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useOsApi } from '@/frontend/hooks/api/use-os-api'
import { useUserSettings } from '@/frontend/hooks/use-user-settings'

type ListMyTasksFilters = {
  q?: string
  status?: TaskListStatus[]
  type?: TaskType
  /** See file header for the `undefined` vs `null` clinic scoping rules. */
  clinicId?: ClinicId | null
  createdAfter?: string
  sortBy?: 'urgency' | 'due_date' | 'created_at'
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

type ListMyTasksOptions = {
  enabled?: boolean
  refreshInterval?: number
}

export function useListMyTasks(filters?: ListMyTasksFilters, options?: ListMyTasksOptions) {
  const api = useOsApi()
  const { settings } = useUserSettings()
  const { region } = useViewerRegion()

  // `null` explicitly disables clinic narrowing; `undefined` falls back to the
  // user's default clinic preference.
  const effectiveClinicId =
    filters?.clinicId === null ? undefined : (filters?.clinicId ?? settings?.defaultClinic?.id)

  // Wait for settings to resolve before firing, so the default-clinic fallback
  // is applied on the first request rather than briefly counting all clinics.
  const enabled = (options?.enabled ?? true) && settings !== undefined

  const queryParams = {
    region,
    q: filters?.q,
    status: filters?.status,
    type: filters?.type,
    clinicId: effectiveClinicId,
    createdAfter: filters?.createdAfter,
    sortBy: filters?.sortBy,
    sortOrder: filters?.sortOrder,
    limit: filters?.limit,
    offset: filters?.offset,
  }

  return useQuery({
    queryKey: ['my-tasks', queryParams],
    enabled,
    refetchInterval: options?.refreshInterval,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await api.get('/tasks/mine', { query: queryParams })
      if (!response.success) throw new Error(response.error.message)
      return response.data
    },
  })
}
